// Canonical "what name should I show" resolver.
//
// The profiles table has six overlapping name columns: display_name,
// preferred_name, full_name, first_name, last_name, username. Different
// surfaces had been doing different fallback orders, which is why some
// pages showed the email prefix even when the user had explicitly entered
// a display name during onboarding (the header was preferring full_name,
// which is auto-populated from the auth metadata to the email local-part
// for some sign-up paths, over display_name).
//
// Use getDisplayedName() everywhere a user-facing name is rendered.

export interface NameProfileLike {
  display_name?: string | null;
  preferred_name?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  email?: string | null;
}

function nonEmpty(s: string | null | undefined): s is string {
  return typeof s === 'string' && s.trim().length > 0;
}

/**
 * Spec'd fallback order:
 *   display_name → preferred_name → full_name → first + last → username → fallback
 *
 * email is intentionally never returned — that's the bug we're fixing.
 */
export function getDisplayedName(
  profile: NameProfileLike | null | undefined,
  fallback = 'User'
): string {
  if (!profile) return fallback;

  if (nonEmpty(profile.display_name)) return profile.display_name.trim();
  if (nonEmpty(profile.preferred_name)) return profile.preferred_name.trim();
  if (nonEmpty(profile.full_name)) return profile.full_name.trim();

  const first = nonEmpty(profile.first_name) ? profile.first_name.trim() : '';
  const last = nonEmpty(profile.last_name) ? profile.last_name.trim() : '';
  if (first || last) return `${first} ${last}`.trim();

  if (nonEmpty(profile.username)) return profile.username.trim();
  return fallback;
}

/**
 * Spec-named alias. New code should import this name; getDisplayedName is
 * kept as an existing-callers alias.
 */
export const resolveDisplayName = getDisplayedName;

/**
 * Back-compat alias kept for the profile-setup checklist surface, which
 * previously preferred the @username form. New callers should prefer
 * resolveDisplayName().
 */
export function getDisplayName(profile?: NameProfileLike | null): string {
  return getDisplayedName(profile, 'there');
}

/**
 * Two-character initials for an avatar fallback. "Bruce Wayne" → "BW",
 * "karl" → "K", "횀 레" → "횀레", "" → "?". Splits on whitespace and
 * takes the first char of the first and last word. Non-Latin glyphs are
 * preserved as-is — international font/initials rendering quality is
 * Phase 2.
 */
export function getInitials(name: string | null | undefined): string {
  if (!nonEmpty(name)) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  const first = parts[0].slice(0, 1);
  const last = parts[parts.length - 1].slice(0, 1);
  return (first + last).toUpperCase();
}
